import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Language as LanguageIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Website } from 'types/website.types';
import { AppDispatch, RootState } from '../../../store';
import { deleteWebsite, setPage, setRowsPerPage, fetchWebsites, fetchWebsitesByPublisher } from '../slices/websitesSlice';
import WebsiteForm from './WebsiteForm';
import ConfirmDialog from './common/ConfirmDialog';
import SmartLogo from 'components/common/SmartLogo';

interface WebsitesTableProps {
  publisherId?: string; // Optional - filters websites by publisher
  openWebsiteId?: string;
  openTab?: 'basic' | 'content' | 'studio' | 'issuecast';
}

const WebsitesTable: React.FC<WebsitesTableProps> = ({ publisherId, openWebsiteId, openTab }) => {
  const { filteredItems, isLoading, pagination } = useSelector((state: RootState) => state.websites);
  const [openForm, setOpenForm] = useState(false);
  const [selectedWebsite, setSelectedWebsite] = useState<Website | undefined>(undefined);
  const [isEdit, setIsEdit] = useState(false);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [websiteToDelete, setWebsiteToDelete] = useState<Website | null>(null);
  const [initialTab, setInitialTab] = useState<number | undefined>(undefined);
  const autoOpenedRef = useRef(false);

  const dispatch = useDispatch<AppDispatch>();

  // Auto-fetch data when mounted or when publisherId changes.
  // Previous implementation skipped fetching if any data already existed in filteredItems.
  // That caused stale results when switching to a different publisher because the first publisher's
  // websites remained in filteredItems and the effect early-returned. We now always fetch on
  // publisherId change (or initial mount) to ensure fresh data.
  useEffect(() => {
    if (publisherId) {
      dispatch(fetchWebsitesByPublisher(publisherId));
    } else {
      dispatch(fetchWebsites());
    }
  }, [dispatch, publisherId]);

  // Reset pagination when publisher changes so we start from the first page of results
  useEffect(() => {
    dispatch(setPage(0));
  }, [publisherId, dispatch]);

  // Auto-open a specific website and tab if requested via props
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (!openWebsiteId) return;
    const w = filteredItems.find(w => w.id === openWebsiteId);
    if (w) {
      setIsEdit(true);
      setSelectedWebsite(w);
      setOpenForm(true);
      // Map tab name to index used by WebsiteForm (Basic=0, Studio=1, IssueCast=2, Content=3)
      const map: Record<string, number> = { basic: 0, studio: 1, issuecast: 2, content: 3 };
      const idx = openTab ? map[openTab] : undefined;
      setInitialTab(idx);
      autoOpenedRef.current = true;

      // After using the query params once, remove them from the URL to avoid re-trigger loops
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('openWebsiteId');
        url.searchParams.delete('openTab');
        const newSearch = url.searchParams.toString();
        const next = `${url.pathname}${newSearch ? `?${newSearch}` : ''}${url.hash}`;
        window.history.replaceState({}, '', next);
      } catch (e) {
        console.warn('Failed to clean URL params after auto-open:', e);
      }
    }
  }, [filteredItems, openWebsiteId, openTab]);

  const handleChangePage = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setRowsPerPage(parseInt(event.target.value, 10)));
  };

  const handleAddClick = () => {
    setIsEdit(false);
    setSelectedWebsite(undefined);
    setOpenForm(true);
    setInitialTab(undefined);
  };

  const handleOpenEdit = (website: Website) => {
    setIsEdit(true);
    setSelectedWebsite(website);
    setOpenForm(true);
    setInitialTab(undefined);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
  };

  const handleDeleteClick = (website: Website, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    setWebsiteToDelete(website);
    setOpenConfirmDelete(true);
  };

  // Handle row click to open editor
  const handleRowClick = (website: Website) => {
    handleOpenEdit(website);
  };

  const handleConfirmDelete = async () => {
    if (websiteToDelete) {
      try {
        await dispatch(deleteWebsite(websiteToDelete.id));
        setOpenConfirmDelete(false);
        setWebsiteToDelete(null);
      } catch (error) {
        console.error('Error deleting website:', error);
      }
    }
  };

  const handleCancelDelete = () => {
    setOpenConfirmDelete(false);
    setWebsiteToDelete(null);
  };

  // Filter items by publisherId if provided
  const items = publisherId
    ? filteredItems.filter((website: { publisherPK: string; }) => website.publisherPK === `PUBLISHER#${publisherId}`)
    : filteredItems;

  // Get currently displayed items based on pagination
  const displayedItems = items.slice(
    pagination.page * pagination.rowsPerPage,
    pagination.page * pagination.rowsPerPage + pagination.rowsPerPage
  );

  // Helper function to render status chip
  const renderStatusChip = (status: string | undefined) => {
    if (status === 'ACTIVE') {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Active"
          color="success"
          size="small"
          variant="outlined"
        />
      );
    } else {
      return (
        <Chip
          icon={<CancelIcon />}
          label="Offline"
          color="default"
          size="small"
          variant="outlined"
        />
      );
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" component="h2">
          News Sources {publisherId ? 'for Selected Publisher' : ''}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          disabled={isLoading}
        >
          Add News Source
        </Button>
      </Box>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table size="medium" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Last Collected</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Categories</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : displayedItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" color="text.secondary">
                      No websites found.
                    </Typography>
                    <Button
                      variant="text"
                      startIcon={<AddIcon />}
                      onClick={handleAddClick}
                      sx={{ mt: 1 }}
                    >
                      Add your first website
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                displayedItems.map((website: Website) => (
                  <TableRow key={website.id} hover onClick={() => handleRowClick(website)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SmartLogo
                          logoUrl={website.logoUrl!}
                          name={website.name}
                        />
                        <Box>
                          {website.tagline && (
                            <Typography variant="body2" color="text.secondary">
                              {website.tagline}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {website.emailAddress ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2">
                            {website.emailAddress}
                          </Typography>
                        </Box>
                      ) : website.url ? (
                        <Box
                          component="a"
                          href={website.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          sx={{
                            textDecoration: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            color: 'primary.main',
                            width: 'fit-content',
                            p: 0.5,
                            borderRadius: 1,
                            '&:hover': {
                              backgroundColor: 'primary.light',
                              color: 'primary.contrastText',
                              '& .MuiSvgIcon-root': {
                                color: 'primary.contrastText',
                              }
                            }
                          }}
                        >
                          <LanguageIcon fontSize="small" sx={{ mr: 1, color: 'inherit' }} />
                          <Typography variant="body2" color="inherit">
                            {website.url.replace(/^https?:\/\//, '')}
                          </Typography>
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {website.lastScraped ? (
                        (() => {
                          const scraped = new Date(website.lastScraped);
                          // Check if date is the Unix epoch (Jan 1, 1970)
                          if (scraped.getTime() === 0) {
                            return '-';
                          }

                          const now = new Date();
                          const diffMs = now.getTime() - scraped.getTime();
                          const diffMinutes = Math.floor(diffMs / 60000);
                          const diffHours = Math.floor(diffMinutes / 60);
                          const diffDays = Math.floor(diffHours / 24);
                          const diffMonths = Math.floor(diffDays / 30);

                          return (
                            <span title={scraped.toLocaleString()}>
                              {diffMinutes < 60
                                ? `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`
                                : diffHours < 24
                                  ? `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
                                  : diffDays < 30
                                    ? `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
                                    : `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`
                              }
                            </span>
                          );
                        })()
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {renderStatusChip(website.status)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {website.categories && website.categories.length > 0 ? (
                          website.categories.map((category: string) => (
                            <Chip
                              key={category}
                              label={category}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No categories
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title="Delete">
                          <IconButton color="error" onClick={(event) => handleDeleteClick(website, event)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={items.length}
          rowsPerPage={pagination.rowsPerPage}
          page={pagination.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Website Form Dialog */}
      <WebsiteForm
        open={openForm}
        onClose={handleCloseForm}
        website={selectedWebsite}
        isEdit={isEdit}
        publisherId={publisherId}
        initialTab={initialTab}
      />

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={openConfirmDelete}
        title="Confirm Delete"
        message={`Are you sure you want to delete ${websiteToDelete?.name}?`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </Box>
  );
};

export default WebsitesTable;